import { omit } from 'lodash'

import {
  INTEGRATION_SERVICE,
  IntegrationService,
  IntellifloCompanyAdviserMapping,
  PlannrCompanyAdviserMapping,
} from '../../types/misc'
import {
  CreateCompanyIntegrationRequest,
  GetCompanyIntegrationLogParams,
  GetCompanyIntegrationStatsParams,
  GetIntellifloAdvisersParams,
  GetPlannrAdvisersParams,
  UpdateIntellifloCompanyIntegrationRequest,
  UpdateNylasCompanyIntegrationRequest,
  UpdatePlannrCompanyIntegrationRequest,
} from '../../types/requests/company-integrations'
import {
  CompanyIntegrationCollectionItem,
  CompanyIntegrationLogCollection,
  CompanyIntegrationStatsCollection,
  IntellifloAdviserCollection,
  IntellifloCampaignCollection,
  IntellifloIntegrationHealthCheck,
  IntellifloIntegrationItem,
  IntellifloIntegrationMetadataItem,
  IntellifloStatusesItem,
  NylasIntegrationItem,
  PlannrAccountCollection,
  PlannrIntegrationAuthInitItem,
  PlannrIntegrationHealthCheck,
  PlannrIntegrationItem,
  PlannrIntegrationMetadataItem,
  PlannrStatusesItem,
} from '../../types/responses/company-integrations'
import apiService from './apiService'

class CompanyIntegrationService {
  private readonly endpoint = '/v1/company-integrations'

  public async getCollection(
    companyId: number,
  ): Promise<CompanyIntegrationCollectionItem> {
    const response = await apiService.get(
      `/v1/companies/${companyId}/integrations`,
    )

    return response.data['hydra:member']
  }

  async getIntellifloIntegration(): Promise<IntellifloIntegrationItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.intelliflo}`,
    )

    return response.data
  }

  async getNylasIntegration(): Promise<NylasIntegrationItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.nylas}`,
    )

    return response.data
  }

  async getPlannrIntegration(): Promise<PlannrIntegrationItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.plannr}`,
    )

    return response.data
  }

  async updatePlannrCompanyIntegration(
    companyIntegrationId: number,
    request: UpdatePlannrCompanyIntegrationRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}`,
      request,
    )

    return response.data
  }

  async enablePlannrIntegration(): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${INTEGRATION_SERVICE.plannr}/enable`,
      {},
    )

    return response.data
  }

  async disablePlannrIntegration(companyIntegrationId: number) {
    const request: UpdatePlannrCompanyIntegrationRequest = {
      enabled: false,
    }

    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}`,
      request,
    )

    return response.data
  }

  async getPlannrIntegrationAuthInit(): Promise<PlannrIntegrationAuthInitItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.plannr}/auth/init`,
    )

    return response.data
  }

  async getPlannrIntegrationMetadata(): Promise<PlannrIntegrationMetadataItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.plannr}/metadata`,
    )

    return response.data
  }

  async getPlannrHealthCheck(): Promise<PlannrIntegrationHealthCheck> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.plannr}/health-check`,
    )

    return response.data
  }

  async getPlannrAdvisers(
    companyIntegrationId: number,
    params?: GetPlannrAdvisersParams,
  ): Promise<PlannrAccountCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/PLANNR/advisers`,
      { params },
    )

    return response.data
  }

  async getPlannrIntroducers(
    companyIntegrationId: number,
    params?: GetPlannrAdvisersParams,
  ): Promise<PlannrAccountCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/PLANNR/introducers`,
      { params },
    )

    return response.data
  }

  async createCompanyIntegration(
    integratedService: IntegrationService,
    request: CreateCompanyIntegrationRequest,
  ) {
    const response = await apiService.post(
      `/v1/companies/integrations/${integratedService}/register`,
      request,
    )

    return response.data
  }

  async updateIntellifloCompanyIntegration(
    companyIntegrationId: number,
    request: UpdateIntellifloCompanyIntegrationRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}`,
      request,
    )

    return response.data
  }

  async updateNylasCompanyIntegration(
    companyIntegrationId: number,
    request: UpdateNylasCompanyIntegrationRequest,
  ) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}`,
      request,
    )

    return response.data
  }

  async enableIntellifloIntegration(): Promise<unknown> {
    const response = await apiService.post(
      `${this.endpoint}/${INTEGRATION_SERVICE.intelliflo}/enable`,
      {},
    )

    return response.data
  }

  async disableIntellifloIntegration(companyIntegrationId: number) {
    const request: UpdateIntellifloCompanyIntegrationRequest = {
      enabled: false,
    }

    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}`,
      request,
    )

    return response.data
  }

  async getIntellifloAdvisers(
    companyIntegrationId: number,
    params?: GetIntellifloAdvisersParams,
  ): Promise<IntellifloAdviserCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/INTELLIFLO/advisers`,
      { params },
    )

    return response.data
  }

  async getIntellifloCampaigns(
    companyIntegrationId: number,
  ): Promise<IntellifloCampaignCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/campaigns`,
    )

    return response.data
  }

  async getIntellifloIntegrationMetadata(): Promise<IntellifloIntegrationMetadataItem> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.intelliflo}/metadata`,
    )

    return response.data
  }

  async getIntellifloHealthCheck(): Promise<IntellifloIntegrationHealthCheck> {
    const response = await apiService.get(
      `${this.endpoint}/${INTEGRATION_SERVICE.intelliflo}/health-check`,
    )

    return response.data
  }

  async getCompanyIntegrationStats(
    companyIntegrationId: number,
    params?: GetCompanyIntegrationStatsParams,
  ): Promise<CompanyIntegrationStatsCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/stats`,
      { params },
    )

    return response.data
  }

  async getCompanyIntegrationLogs(
    companyIntegrationId: number,
    params?: GetCompanyIntegrationLogParams,
  ): Promise<CompanyIntegrationLogCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/logs`,
      { params },
    )

    return response.data
  }

  async disconnectCompanyEmail(companyIntegrationId: number) {
    const response = await apiService.patch(
      `${this.endpoint}/${companyIntegrationId}/disconnect-company-email`,
      {},
    )

    return response.data
  }

  async getIntellifloStatuses(
    companyIntegrationId: number,
  ): Promise<IntellifloStatusesItem> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/INTELLIFLO/statuses`,
    )

    return response.data
  }

  async getPlannrStatuses(
    companyIntegrationId: number,
  ): Promise<PlannrStatusesItem> {
    const response = await apiService.get(
      `${this.endpoint}/${companyIntegrationId}/PLANNR/statuses`,
    )

    return response.data
  }

  buildUpdateIntellifloAdviserMappingRequest(
    args: BuildUpdateIntellifloAdviserMappingRequestArgs,
  ): UpdateIntellifloCompanyIntegrationRequest {
    const { integration, companyId, rqUserId, intellifloAdviserId } = args
    const currentAdviserMapping = integration.config.adviserMapping

    const companyAdviserMapping = currentAdviserMapping[companyId] || {}
    const newCompanyAdviserMapping = intellifloAdviserId
      ? { ...companyAdviserMapping, [rqUserId]: intellifloAdviserId }
      : (omit(
          companyAdviserMapping,
          rqUserId,
        ) as IntellifloCompanyAdviserMapping)

    return {
      config: {
        ...integration.config,
        adviserMapping: {
          ...currentAdviserMapping,
          [companyId]: newCompanyAdviserMapping,
        },
      },
    }
  }

  buildUpdatePlannrAdviserMappingRequest(
    args: BuildUpdatePlannrAdviserMappingRequestArgs,
  ): UpdatePlannrCompanyIntegrationRequest {
    const { integration, companyId, rqUserId, plannrAdviserId } = args
    const currentAdviserMapping = integration.config.adviserMapping

    const companyAdviserMapping = currentAdviserMapping[companyId] || {}
    const newCompanyAdviserMapping = plannrAdviserId
      ? { ...companyAdviserMapping, [rqUserId]: plannrAdviserId }
      : (omit(companyAdviserMapping, rqUserId) as PlannrCompanyAdviserMapping)

    return {
      config: {
        ...integration.config,
        adviserMapping: {
          ...currentAdviserMapping,
          [companyId]: newCompanyAdviserMapping,
        },
      },
    }
  }
}

export interface BuildUpdateIntellifloAdviserMappingRequestArgs {
  integration: IntellifloIntegrationItem
  companyId: number
  rqUserId: number
  intellifloAdviserId?: number
}

export interface BuildUpdatePlannrAdviserMappingRequestArgs {
  integration: PlannrIntegrationItem
  companyId: number
  rqUserId: number
  plannrAdviserId?: string
}

const companyIntegrationService = new CompanyIntegrationService()

export default companyIntegrationService
